export interface SimulationSize {
  width: number;
  height: number;
}

export interface FluidImpulse {
  /** Normalized viewport position, with y increasing downward. */
  point: readonly [number, number];
  /** Velocity added in simulation cells per second. */
  velocity: readonly [number, number];
  /** Radius in simulation cells. */
  radius: number;
  /** Premultiplied visual dye colour in linear-ish display space. */
  color: readonly [number, number, number];
  dyeAmount: number;
}

export interface SolverSettings {
  pressureIterations: number;
  /** Kinematic viscosity in viewport-height² / second. */
  kinematicViscosity: number;
  viscosityIterations: number;
  velocityDissipation: number;
  dyeDissipation: number;
  curlStrength: number;
  /** Prescribed right-to-left inlet speed in viewport-heights per second. */
  inletSpeed: number;
}

export interface ObstacleMask {
  width: number;
  height: number;
  data: Uint8Array;
}

export const DEFAULT_SOLVER_SETTINGS: SolverSettings = {
  pressureIterations: 24,
  kinematicViscosity: 0.000055,
  viscosityIterations: 8,
  velocityDissipation: 1,
  dyeDissipation: 0.996,
  curlStrength: 1,
  inletSpeed: 0.3,
};

export function chooseSimulationSize(
  viewportWidth: number,
  viewportHeight: number,
): SimulationSize {
  if (viewportWidth <= 0 || viewportHeight <= 0) {
    return { width: 256, height: 144 };
  }

  const longEdge =
    viewportWidth < 700
      ? 256
      : viewportWidth > 1800
        ? 640
        : 512;
  const aspect = viewportWidth / viewportHeight;

  if (aspect >= 1) {
    return {
      width: alignTo(longEdge, 8),
      height: alignTo(Math.max(96, longEdge / aspect), 8),
    };
  }

  return {
    width: alignTo(Math.max(96, longEdge * aspect), 8),
    height: alignTo(longEdge, 8),
  };
}

function alignTo(value: number, alignment: number): number {
  return Math.max(alignment, Math.round(value / alignment) * alignment);
}
